import { useQuery } from "@apollo/client";
import { ArrowUp as ArrowUpIcon } from "@tamagui/lucide-icons";
import { Badge, Body2, Display3, isWeb, Modal, YStack } from "@medbillai/ui";
import { useRouter } from "@medbillai/navigation/router";
import { accountsLinkWebAgentRelinkUrl, accountsUrl, coverageUrl } from "@medbillai/utils/urls";
import { useAccountLinkDataStore } from "../../../lib/accounts-link/accountLinkStore";
import { useStartAccountLink } from "../../../lib/accounts-link/utils";
import { analyticsService } from "../../../lib/analytics";
import { errorHandling } from "../../../lib/apollo/utils";
import { usePushGetHelp } from "../../../lib/usePushGetHelp";
import { linksQuery } from "../../accounts-link/utils/queries";
import { money, shortDate } from "../format";
import type { CoveragePlan, Movement } from "../read/readCoverage";
import { isInsurerPortalLink } from "../useInsuranceLinked";
import { useViewed } from "../useViewed";
type Named = Pick<Movement, "kind" | "family" | "outOfNetwork">;

/** "family out-of-network deductible", as far as the lead says. */
export const movementFigure = ({
  kind,
  family,
  outOfNetwork
}: Named) => [family ? "family" : null, outOfNetwork ? "out-of-network" : null, kind === "deductible" ? "deductible" : "out-of-pocket max"].filter(Boolean).join(" ");
export const movementTitle = (movement: Named) => `Your ${movementFigure(movement)} moved`;
const ADD_BILL_SOURCE = "coverage_movement_add_bill";
export function MovementSheet({
  plan,
  movement,
  open,
  onOpenChange
}: {
  plan: CoveragePlan;
  movement: Movement;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const {
    pushGetHelp
  } = usePushGetHelp();
  const startAccountLink = useStartAccountLink();
  const relinkUrl = useRelinkUrl();
  const resetLink = useAccountLinkDataStore(state => state.reset);
  const setLinkExitUrl = useAccountLinkDataStore(state => state.setExitUrl);
  const setLinkStartedFrom = useAccountLinkDataStore(state => state.setStartedFrom);
  const {
    push
  } = useRouter();
  const close = () => onOpenChange(false);
  return <Modal open={open} onOpenChange={onOpenChange} headerProps={{
    title: "What we noticed",
    variant: "titleLeft",
    // A full-screen web dialog needs its close button.
    hideCloseButton: !isWeb
  }}
  // Small keeps the footer stacked at every width.
  size="small" footerProps={{
    primaryButtonProps: relinkUrl ? {
      children: "Re-link your insurance",
      onPress: () => {
        analyticsService.trackEvent("CoverageMovementCtaTapped", {
          cta: "relinkInsurance"
        });
        // With several portals this opens Accounts to pick one, not the relink flow.
        if (relinkUrl !== accountsUrl()) {
          analyticsService.trackEvent("InsuranceLinkingStarted", {
            source: "coverage_movement_relink"
          });
        }
        close();
        resetLink();
        setLinkExitUrl(coverageUrl());
        setLinkStartedFrom("coverage_movement_relink");
        push(relinkUrl);
      }
    } : {
      children: "Link your insurance",
      onPress: () => {
        analyticsService.trackEvent("CoverageMovementCtaTapped", {
          cta: "linkInsurance"
        });
        analyticsService.trackEvent("InsuranceLinkingStarted", {
          source: "coverage_movement"
        });
        close();
        startAccountLink({
          exitUrl: coverageUrl(),
          startedFrom: "coverage_movement"
        });
      }
    },
    secondaryButtonProps: {
      children: "Upload the bill",
      onPress: () => {
        analyticsService.trackEvent("CoverageMovementCtaTapped", {
          cta: "addBill"
        });
        close();
        pushGetHelp({
          caseType: "BILL_ISSUE_V2",
          title: "Get help with a bill",
          source: ADD_BILL_SOURCE
        });
      }
    }
  }} surface="surface2" data-sentry-element="Modal" data-sentry-component="MovementSheet" data-sentry-source-file="MovementSheet.tsx">
      <MovementPage plan={plan} movement={movement} open={open} data-sentry-element="MovementPage" data-sentry-source-file="MovementSheet.tsx" />
    </Modal>;
}

// Nothing ties a plan to the portal its claims come from, so with several
// insurer portals the user picks one on the accounts page.
const useRelinkUrl = (): string | undefined => {
  const {
    data
  } = useQuery(linksQuery, {
    context: errorHandling("caller")
  });
  const portals = data?.me?.webAgentLinks?.filter(isInsurerPortalLink) ?? [];
  const [only] = portals;
  if (portals.length > 1) {
    return accountsUrl();
  }
  return only ? accountsLinkWebAgentRelinkUrl({
    linkId: only.id
  }) : undefined;
};
function MovementPage({
  plan,
  movement,
  open
}: {
  plan: CoveragePlan;
  movement: Movement;
  open: boolean;
}) {
  useViewed("movement", open ? plan : null);
  const amount = `+${money(movement.delta)} · noticed ${shortDate(movement.at)}`;
  return <YStack paddingTop="$sp0.75" paddingBottom="$sp2" data-sentry-element="YStack" data-sentry-component="MovementPage" data-sentry-source-file="MovementSheet.tsx">
      <YStack alignItems="center" paddingHorizontal="$sp1" data-sentry-element="YStack" data-sentry-source-file="MovementSheet.tsx">
        <YStack width="$si7" height="$si7" borderRadius="$br2" backgroundColor="$bgWarning" alignItems="center" justifyContent="center" aria-hidden data-sentry-element="YStack" data-sentry-source-file="MovementSheet.tsx">
          <ArrowUpIcon size="$iconLarge" color="$iconWarning" data-sentry-element="ArrowUpIcon" data-sentry-source-file="MovementSheet.tsx" />
        </YStack>
        <Display3 color="$textDefault" textAlign="center" marginTop="$sp1" marginBottom="$sp1" data-sentry-element="Display3" data-sentry-source-file="MovementSheet.tsx">
          {movementTitle(movement)}
        </Display3>
        <YStack alignSelf="center" data-sentry-element="YStack" data-sentry-source-file="MovementSheet.tsx">
          <Badge status="warning" bordered={false} data-sentry-element="Badge" data-sentry-source-file="MovementSheet.tsx">
            {amount}
          </Badge>
        </YStack>
        <Body2 color="$textSubtle" textAlign="center" marginTop="$sp1.5" data-sentry-element="Body2" data-sentry-source-file="MovementSheet.tsx">
          {plan.payerName} counted more of{" "}
          {movement.family ? "your family’s care" : "your care"} toward your{" "}
          {movementFigure(movement)}. A bill for that care may be on its way.
        </Body2>
      </YStack>
    </YStack>;
}